import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { usePermissions } from '@/hooks/usePermissions';
import { AssessmentCatalogCard } from '@/components/assessments/AssessmentCatalogCard';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { listResource } from '@/lib/api';

interface Assessment {
  id: string;
  title: string;
  code?: string;
  status?: string;
  type?: string;
  versionCount?: number;
  courseInstancesCount?: number;
  usageCount?: number;
  questionCount?: number;
  currentVersion?: { questionCount?: number };
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicada',
  archived: 'Archivada',
};

export function EvaluacionesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const appliedSearch = useDebouncedValue(search, 300).trim();
  const { hasPermission } = usePermissions();
  const canCreateAssessment = hasPermission(PERMISSIONS.EVALUACIONES_CREAR);

  const { data, isLoading, error } = useQuery({
    queryKey: ['assessments', appliedSearch],
    queryFn: () =>
      listResource<Assessment>('/assessments', {
        perPage: 20,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    return rows.filter((row) => !status || row.status === status);
  }, [rows, status]);

  const hasActiveFilters = Boolean(search.trim() || status);

  return (
    <div className="lms-course-catalog lms-assessment-catalog lms-site-admin-hub-page font-sans">
      <header className="lms-assessment-catalog__header">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Biblioteca de Evaluaciones</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Evaluaciones reutilizables, desacopladas de cursos.
            </p>
          </div>
        </div>
        <div className="lms-assessment-catalog__header-actions">
          <Link to="/preguntas" className="lms-assessment-catalog__btn lms-assessment-catalog__btn--secondary">
            <span className="material-symbols-outlined" aria-hidden="true">
              library_books
            </span>
            Banco de Preguntas
          </Link>
          {canCreateAssessment && (
            <Link to="/evaluaciones/create" className="lms-assessment-catalog__btn lms-assessment-catalog__btn--primary">
              <span className="material-symbols-outlined" aria-hidden="true">
                add
              </span>
              Nueva evaluación
            </Link>
          )}
        </div>
      </header>

      <form method="get" action="/evaluaciones" className="lms-assessment-catalog__filters" onSubmit={(event) => event.preventDefault()}>
        <div className="lms-assessment-catalog__filter-field lms-assessment-catalog__filter-field--search">
          <label htmlFor="assessment-search">Buscar</label>
          <SearchInput
            id="assessment-search"
            name="search"
            value={search}
            onChange={setSearch}
            placeholder="Buscar por título o ID..."
            aria-label="Buscar evaluaciones"
            className="lms-assessment-catalog__search"
          />
        </div>

        <div className="lms-assessment-catalog__filter-field">
          <label htmlFor="assessment-status">Estado</label>
          <select id="assessment-status" name="status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="draft">Borrador</option>
            <option value="published">Publicada</option>
            <option value="archived">Archivada</option>
          </select>
        </div>

        <div className="lms-assessment-catalog__filter-field">
          <label htmlFor="assessment-category">Categoría</label>
          <select id="assessment-category" name="category_id" defaultValue="">
            <option value="">Todas</option>
          </select>
        </div>

        <div className="lms-assessment-catalog__filter-actions">
          {hasActiveFilters && (
            <Link
              to="/evaluaciones"
              className="lms-assessment-catalog__filter-clear"
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
            >
              Quitar filtros
            </Link>
          )}
        </div>
      </form>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : filtered.length === 0 ? (
        hasActiveFilters ? (
          <SearchEmptyState
            term={search.trim()}
            onClear={() => {
              setSearch('');
              setStatus('');
            }}
          />
        ) : (
        <div className="lms-assessment-catalog__empty">
          <span className="material-symbols-outlined lms-assessment-catalog__empty-icon" style={{ fontVariationSettings: "'FILL' 1" }} aria-hidden="true">
            quiz
          </span>
          <h2>Sin evaluaciones</h2>
          <p>Crea una evaluación reutilizable para referenciarla en cualquier curso.</p>
          {canCreateAssessment && (
            <Link to="/evaluaciones/create" className="lms-assessment-catalog__btn lms-assessment-catalog__btn--primary">
              <span className="material-symbols-outlined" aria-hidden="true">
                add
              </span>
              Nueva evaluación
            </Link>
          )}
        </div>
        )
      ) : (
        <div className="lms-assessment-catalog__grid">
          {filtered.map((assessment) => (
            <AssessmentCatalogCard
              key={assessment.id}
              id={assessment.id}
              title={assessment.title}
              code={assessment.code}
              typeLabel="Examen"
              statusLabel={STATUS_LABELS[assessment.status ?? ''] ?? assessment.status}
              versionCount={assessment.versionCount ?? 1}
              courseCount={assessment.courseInstancesCount ?? assessment.usageCount ?? 0}
              questionCount={assessment.currentVersion?.questionCount ?? assessment.questionCount ?? 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
